import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Lightbox from 'react-lightbox-component';
import 'react-lightbox-component/build/css/index.css';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';

// Styled Components
const GalleryPageContainer = styled.div`
  padding: 5rem 0;
`;

const PageTitle = styled.h1`
  text-align: center;
  font-size: 3rem;
  margin-bottom: 3rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: #f59e0b;
  }
`;

const CategoryTabs = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const CategoryTab = styled.button`
  padding: 0.75rem 1.5rem;
  margin: 0.5rem;
  background-color: ${props => props.active ? '#1e3a8a' : '#e5e7eb'};
  color: ${props => props.active ? 'white' : '#4b5563'};
  border: none;
  border-radius: 50px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.active ? '#1e40af' : '#d1d5db'};
  }
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const GalleryItem = styled.div`
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  height: 250px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
`;

// Loading state component
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
`;

const LoadingSpinner = styled.div`
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-top: 4px solid #1e3a8a;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Empty state component
const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 0;
  color: #6b7280;
`;

const Gallery = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Fetch gallery images from Firebase
  useEffect(() => {
    const fetchGalleryImages = async () => {
      try {
        const galleryCollection = collection(db, 'galleryImages');
        const gallerySnapshot = await getDocs(galleryCollection);
        const galleryList = gallerySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setGalleryImages(galleryList);
      } catch (error) {
        console.error('Error fetching gallery images:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchGalleryImages();
  }, []);
  
  // Get unique categories
  const categories = ['All', ...new Set(galleryImages.map(img => img.category || 'Uncategorized'))];
  
  // Filter images by category
  const filteredImages = activeCategory === 'All'
    ? galleryImages
    : galleryImages.filter(img => img.category === activeCategory);
  
  // Format images for lightbox
  const lightboxImages = filteredImages.map(img => ({
    src: img.url,
    title: img.title,
    description: img.category
  }));
  
  const handleImageClick = (index) => {
    setLightboxIndex(index);
    setShowLightbox(true);
  };
  
  return (
    <GalleryPageContainer>
      <PageTitle>Our Gallery</PageTitle>
      
      {loading ? (
        <LoadingContainer>
          <LoadingSpinner />
        </LoadingContainer>
      ) : galleryImages.length === 0 ? (
        <EmptyState>
          <h3>No gallery images found</h3>
          <p>Check back soon for updates to our gallery!</p>
        </EmptyState>
      ) : (
        <>
          <CategoryTabs>
            {categories.map((category, index) => (
              <CategoryTab 
                key={index}
                active={activeCategory === category}
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </CategoryTab>
            ))}
          </CategoryTabs>
          
          <GalleryGrid>
            {filteredImages.length > 0 ? (
              filteredImages.map((image, index) => (
                <GalleryItem key={image.id} onClick={() => handleImageClick(index)}>
                  <img src={image.url} alt={image.title || 'Gallery image'} />
                </GalleryItem>
              ))
            ) : (
              <EmptyState>
                <p>No images found in the {activeCategory} category</p>
              </EmptyState>
            )}
          </GalleryGrid>
        </>
      )}
      
      {showLightbox && (
        <Lightbox 
          images={lightboxImages}
          startIndex={lightboxIndex}
          onClose={() => setShowLightbox(false)}
        />
      )}
    </GalleryPageContainer>
  );
};

export default Gallery;
